export const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

export const toNumber = (value, fallback = 0) => {
  const numeric = Number(String(value ?? '').replace(/,/g, '').trim());
  return Number.isFinite(numeric) ? numeric : fallback;
};

export const clampPercent = (value) => Math.min(100, Math.max(0, toNumber(value)));

export const calculatePackPricing = ({
  finalCost,
  units,
  unitCost,
  marginPercent,
  proportionFactor = 1,
  discountPercent,
  fixedMrp,
} = {}) => {
  const unitCount = toNumber(units);
  const totalCost = toNumber(finalCost);
  const costPerPiece =
    totalCost > 0 && unitCount > 0
      ? Math.ceil(totalCost / unitCount)
      : unitCost > 0
        ? Math.ceil(toNumber(unitCost))
        : 0;
  const effectiveMarginPercent = toNumber(marginPercent) * toNumber(proportionFactor, 1);
  const dprice =
    costPerPiece > 0
      ? Math.ceil(costPerPiece + (costPerPiece * effectiveMarginPercent) / 100)
      : 0;
  const discount = clampPercent(discountPercent);
  const mrp = toNumber(fixedMrp) > 0 ? toNumber(fixedMrp) : dprice > 0 && discount > 0 && discount < 100
    ? Math.ceil(dprice / (1 - discount / 100))
    : dprice;
  const totalUnits = unitCount > 0 ? unitCount : 1;
  const dpriceTotal = dprice * totalUnits;
  const mrpTotal = mrp * totalUnits;
  const effectiveDiscountPercent =
    mrp > 0 && dprice > 0 ? ((mrp - dprice) / mrp) * 100 : 0;
  const marginAmount = totalCost > 0 ? dpriceTotal - totalCost : dprice - costPerPiece;
  const finalMarginPercent =
    totalCost > 0 ? (marginAmount / totalCost) * 100 : effectiveMarginPercent;

  return {
    costPerPiece,
    dprice,
    mrp,
    dpriceTotal,
    mrpTotal,
    effectiveDiscountPercent,
    effectiveMarginPercent,
    finalMarginPercent,
    marginAmount,
  };
};

export const calculatePackPricingFromRow = (row = {}) => {
  const hasLandedCost = toNumber(row.landed_purchase_amount || row.landedPurchaseAmount) > 0;
  const pricingUnits = firstValue(
    row.landed_purchase_units,
    row.landedPurchaseUnits,
    row.purchase_units,
    row.purchaseUnits,
    !hasLandedCost
      ? firstValue(row.no_of_units, row.noOfUnits, row.count_in_stock, row.countInStock, row.purchase_qty, row.purchaseQty)
      : undefined
  );
  const basePurchaseAmount =
    toNumber(row.base_purchase_amount) ||
    (toNumber(row.unit_price) *
      toNumber(firstValue(pricingUnits, row.no_of_units, row.noOfUnits, row.count_in_stock, row.countInStock, row.purchase_qty, row.purchaseQty)));
  const detailedExtraCost =
    toNumber(row.purchase_extra_cost) + toNumber(row.inventory_extra_cost);
  const extraCost = detailedExtraCost > 0 ? detailedExtraCost : toNumber(row.additional_cost_total);
  const calculatedTotalCost =
    basePurchaseAmount + extraCost;
  const finalCost = toNumber(
    firstValue(
      row.landed_purchase_amount,
      row.landedPurchaseAmount,
      row.current_landed_purchase_amount,
      row.currentLandedPurchaseAmount,
      row.current_stock_landed_cost,
      row.currentStockLandedCost,
      row.final_cost,
      row.finalCost,
      calculatedTotalCost > 0 ? calculatedTotalCost : undefined
    )
  );

  return calculatePackPricing({
    fixedMrp: firstValue(row.unit_mrp, row.unit_MRP, row.inventory_unit_mrp),
    finalCost,
    units: pricingUnits,
    unitCost: firstValue(
      row.landed_purchase_unit_price,
      row.landedPurchaseUnitPrice,
      row.purchase_price_with_costs,
      row.purchasePriceWithCosts,
      row.unit_price,
      row.unitPrice
    ),
    marginPercent: firstValue(row.base_margin_percent, row.configured_margin_percent, row.margin_percent, row.marginPercent),
    proportionFactor: firstValue(row.proportion_factor, row.proportionFactor, 1),
    discountPercent: firstValue(row.configured_discount_percent, row.discount_percent, row.discountPercent),
  });
};
